import { useEffect, useState } from "react"
import { LuExternalLink } from "react-icons/lu"
import { Box, HStack, Input, Stack, Textarea } from "@chakra-ui/react"
import { FaChevronDown, FaChevronRight } from "react-icons/fa"
import { Button, Text } from "@/components"
import { DealClientInfo as DealClientInfoType, DealClient } from "@/types"

interface Props {
  clientInfo: DealClientInfoType | null
  client?: DealClient | null
  onUpdate?: (field: keyof DealClientInfoType, value: string | number | null) => void
  onClientUpdate?: (field: keyof DealClient, value: string | number | null) => void
  /** When true, renders without the outer card shell and title — for embedding in a panel */
  inline?: boolean
}

type SectionType = "contact" | "personal" | "clientBanking" | "companyExercise" | "closing"

interface FieldConfig<T> {
  key: keyof T
  label: string
  type: "text" | "currency" | "date" | "boolean" | "number"
}

const SECTION_CONFIG: Record<SectionType, { label: string; color: string; bgColor: string; borderColor: string }> = {
  contact: { label: "Contact Information", color: "teal.700", bgColor: "teal.50", borderColor: "teal.200" },
  personal: { label: "Personal Details", color: "blue.700", bgColor: "blue.50", borderColor: "blue.200" },
  clientBanking: { label: "Client Banking", color: "purple.700", bgColor: "purple.50", borderColor: "purple.200" },
  companyExercise: { label: "Company Exercise Information", color: "green.700", bgColor: "green.50", borderColor: "green.200" },
  closing: { label: "Closing Details", color: "orange.700", bgColor: "orange.50", borderColor: "orange.200" },
}

// DealClient fields (contact info)
const CLIENT_FIELDS: FieldConfig<DealClient>[] = [
  { key: "firstName", label: "First Name", type: "text" },
  { key: "lastName", label: "Last Name", type: "text" },
  { key: "middleName", label: "Middle Name", type: "text" },
  { key: "legalName", label: "Legal Name", type: "text" },
  { key: "email", label: "Email", type: "text" },
  { key: "phoneNumber", label: "Phone", type: "text" },
]

// DealClientInfo fields by section
const SECTION_FIELDS: Record<Exclude<SectionType, "contact">, FieldConfig<DealClientInfoType>[]> = {
  personal: [
    { key: "title", label: "Title", type: "text" },
    { key: "salary", label: "Salary", type: "currency" },
    { key: "marriageStatus", label: "Marriage Status", type: "text" },
    { key: "referralSource", label: "Referral Source", type: "text" },
  ],
  clientBanking: [
    { key: "clientBankName", label: "Bank Name", type: "text" },
    { key: "clientBankAccountNumber", label: "Account Number", type: "text" },
    { key: "clientBankRoutingNumber", label: "Routing Number", type: "text" },
  ],
  companyExercise: [
    { key: "companyBankName", label: "Bank Name", type: "text" },
    { key: "companyBankAccountNumber", label: "Account Number", type: "text" },
    { key: "companyBankRoutingNumber", label: "Routing Number", type: "text" },
    { key: "exerciseOwnerName", label: "Exercise Owner", type: "text" },
    { key: "companyStreetAddress", label: "Street Address", type: "text" },
    { key: "companyCity", label: "City", type: "text" },
    { key: "companyState", label: "State", type: "text" },
    { key: "companyZip", label: "Zip", type: "text" },
  ],
  closing: [
    { key: "checkPayableTo", label: "Check Payable To", type: "text" },
    { key: "courierNotes", label: "Courier Notes", type: "text" },
  ],
}

interface SectionTableProps<T> {
  section: SectionType
  fields: FieldConfig<T>[]
  localData: Record<string, string | number | null>
  isExpanded: boolean
  onToggleExpand: () => void
  onFieldChange: (field: keyof T, value: string | number | null) => void
}

const SectionTable = <T,>({
  section,
  fields,
  localData,
  isExpanded,
  onToggleExpand,
  onFieldChange,
}: SectionTableProps<T>) => {
  const config = SECTION_CONFIG[section]

  const getInputValue = (key: string, fieldType: string): string => {
    const value = localData[key]
    if (value === null || value === undefined) return ""
    // For date fields, extract just the date portion (YYYY-MM-DD)
    if (fieldType === "date" && typeof value === "string" && value.includes("T")) {
      return value.split("T")[0]
    }
    return String(value)
  }

  const filledCount = fields.filter((f) => {
    const value = localData[f.key as string]
    return value !== null && value !== undefined && value !== ""
  }).length

  return (
    <Box border="1px" borderColor={config.borderColor} borderRadius={8} overflow="hidden">
      {/* Header */}
      <HStack
        px={2}
        py={1.5}
        bg={config.bgColor}
        cursor="pointer"
        onClick={onToggleExpand}
        justifyContent="space-between"
        _hover={{ opacity: 0.9 }}
      >
        <HStack gap={2}>
          {isExpanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
          <Text fontSize="xs" fontWeight="bold" color={config.color}>
            {config.label}
          </Text>
        </HStack>
        <Text fontSize="xs" color="text.muted">
          {filledCount}/{fields.length}
        </Text>
      </HStack>

      {/* Field stack */}
      {isExpanded && (
        <Stack gap={3} p={3} bg="card.bg">
          {fields.map((field) => {
            const value = localData[field.key as string]

            return (
              <Stack key={field.key as string} gap={1}>
                <Text fontSize="2xs" fontWeight="semibold" color="text.muted" textTransform="uppercase" letterSpacing="0.04em">
                  {field.label}
                </Text>
                {field.type === "boolean" ? (
                  <HStack gap={2}>
                    <Button
                      size="xs"
                      variant={value === 1 ? "solid" : "outline"}
                      colorScheme={value === 1 ? "green" : "gray"}
                      onClick={() => onFieldChange(field.key, 1)}
                    >
                      Yes
                    </Button>
                    <Button
                      size="xs"
                      variant={value === 0 ? "solid" : "outline"}
                      colorScheme={value === 0 ? "red" : "gray"}
                      onClick={() => onFieldChange(field.key, 0)}
                    >
                      No
                    </Button>
                    <Button
                      size="xs"
                      variant={value === null || value === undefined ? "solid" : "outline"}
                      onClick={() => onFieldChange(field.key, null)}
                    >
                      N/A
                    </Button>
                  </HStack>
                ) : (
                  <Input
                    size="sm"
                    value={getInputValue(field.key as string, field.type)}
                    onChange={(e) => {
                      const inputValue = e.target.value
                      let convertedValue: string | number | null = inputValue

                      if (inputValue === "") {
                        convertedValue = null
                      } else if (field.type === "number" || field.type === "currency") {
                        const parsed = parseInt(inputValue, 10)
                        convertedValue = isNaN(parsed) ? inputValue : parsed
                      }

                      onFieldChange(field.key, convertedValue)
                    }}
                    type={field.type === "number" || field.type === "currency" ? "number" : field.type === "date" ? "date" : "text"}
                    placeholder={field.label}
                    variant="flushed"
                    _placeholder={{ color: "gray.400" }}
                  />
                )}
              </Stack>
            )
          })}
        </Stack>
      )}
    </Box>
  )
}

export const DealClientInfo = ({ clientInfo, client, onUpdate, onClientUpdate, inline }: Props) => {
  const [expandedSections, setExpandedSections] = useState<Set<SectionType>>(
    new Set<SectionType>([])
  )

  // Local state for DealClientInfo fields
  const [localData, setLocalData] = useState<Record<string, string | number | null>>({})
  // Local state for DealClient fields
  const [localClientData, setLocalClientData] = useState<Record<string, string | number | null>>({})
  // Local state for notes
  const [localNotes, setLocalNotes] = useState<string>("")

  // Sync local state with clientInfo prop
  useEffect(() => {
    if (clientInfo) {
      const data: Record<string, string | number | null> = {}
      Object.values(SECTION_FIELDS)
        .flat()
        .forEach((field) => {
          data[field.key] = clientInfo[field.key] as string | number | null
        })
      setLocalData(data)
      setLocalNotes(clientInfo.notes || "")
    }
  }, [clientInfo])

  // Sync local state with client prop
  useEffect(() => {
    if (client) {
      const data: Record<string, string | number | null> = {}
      CLIENT_FIELDS.forEach((field) => {
        data[field.key] = client[field.key] as string | number | null
      })
      setLocalClientData(data)
    }
  }, [client])

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString()
  }

  const toggleSection = (section: SectionType) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(section)) {
      newExpanded.delete(section)
    } else {
      newExpanded.add(section)
    }
    setExpandedSections(newExpanded)
  }

  const expandAll = () => {
    setExpandedSections(new Set<SectionType>(["contact", "personal", "clientBanking", "companyExercise", "closing"]))
  }

  const collapseAll = () => {
    setExpandedSections(new Set())
  }

  const handleFieldChange = (field: keyof DealClientInfoType, value: string | number | null) => {
    setLocalData((prev) => ({
      ...prev,
      [field]: value,
    }))

    if (onUpdate) {
      onUpdate(field, value)
    }
  }

  const handleClientFieldChange = (field: keyof DealClient, value: string | number | null) => {
    setLocalClientData((prev) => ({
      ...prev,
      [field]: value,
    }))

    if (onClientUpdate) {
      onClientUpdate(field, value)
    }
  }

  const handleNotesChange = (value: string) => {
    setLocalNotes(value)
    if (onUpdate) {
      onUpdate("notes", value || null)
    }
  }

  if (!clientInfo && !client) {
    if (inline) return <Text fontSize="sm" color="text.muted">No client information found.</Text>
    return (
      <Box w="100%" p={5} border="1px" borderColor="gray.200" borderRadius={10} boxShadow="sm" bg="card.bg">
        <Text fontSize="lg" fontWeight="bold" mb={4}>
          Client Information
        </Text>
        <Text color="text.muted">No client information found.</Text>
      </Box>
    )
  }

  const content = (
    <>
      {/* LinkedIn Profile */}
      {client?.clientData?.linkedinUrl && (
        <Box mb={3}>
          <a
            href={client.clientData.linkedinUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
          >
            <Text fontSize="sm" fontWeight="medium" color="#0A66C2" _hover={{ textDecoration: "underline" }}>
              LinkedIn Profile
            </Text>
            <LuExternalLink size={14} color="#0A66C2" />
          </a>
        </Box>
      )}

      {/* Notes Section */}
      {clientInfo && (
        <Box mb={3}>
          <Text fontSize="xs" fontWeight="semibold" color="text.muted" mb={1}>Notes</Text>
          <Textarea
            size="sm"
            value={localNotes}
            onChange={(e) => handleNotesChange(e.target.value)}
            placeholder="Add notes about the client..."
            rows={3}
            resize="vertical"
          />
        </Box>
      )}

      <Stack gap={2}>
        {/* Contact Information (DealClient) */}
        {client && (
          <SectionTable<DealClient>
            section="contact"
            fields={CLIENT_FIELDS}
            localData={localClientData}
            isExpanded={expandedSections.has("contact")}
            onToggleExpand={() => toggleSection("contact")}
            onFieldChange={handleClientFieldChange}
          />
        )}

        {/* DealClientInfo sections */}
        {clientInfo && (["personal", "clientBanking", "companyExercise", "closing"] as const).map((section) => (
          <SectionTable<DealClientInfoType>
            key={section}
            section={section}
            fields={SECTION_FIELDS[section]}
            localData={localData}
            isExpanded={expandedSections.has(section)}
            onToggleExpand={() => toggleSection(section)}
            onFieldChange={handleFieldChange}
          />
        ))}
      </Stack>

      {clientInfo && (
        <Text fontSize="xs" color="text.muted" mt={3}>
          Last modified: {formatDate(clientInfo.updatedAt)}
        </Text>
      )}
    </>
  )

  if (inline) {
    return <Stack gap={0}>{content}</Stack>
  }

  return (
    <Box w="100%" p={5} border="1px" borderColor="gray.200" borderRadius={10} boxShadow="sm" bg="card.bg">
      <HStack mb={4} justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Text fontSize="lg" fontWeight="bold">
          Client Information
        </Text>
        <Button
          size="xs"
          variant="outline"
          onClick={expandedSections.size === 5 ? collapseAll : expandAll}
        >
          {expandedSections.size === 5 ? "Collapse All" : "Expand All"}
        </Button>
      </HStack>
      {content}
    </Box>
  )
}
